import { useEffect } from "react";
import { showToastWithProps } from "../utils/toastUtils";

interface VersionWatcherProps {
  currentVersion: string;
  checkIntervalMs?: number;
}

/**
 * Periodically checks /version.json (no-store) and forces a reload
 * when the fetched version differs from the current one.
 */
export default function VersionWatcher({
  currentVersion,
  checkIntervalMs = 60_000,
}: VersionWatcherProps) {
  useEffect(() => {
    let timer: number | undefined;

    const normalize = (v: string) => (v || "").replace(/^v/i, "");
    const current = normalize(currentVersion);
    const reloadedKey = "__reloaded_for_version__";
    const promptedKey = "__prompted_update_version__";

    const check = async () => {
      try {
        const res = await fetch(`/version.json?ts=${Date.now()}`, {
          cache: "no-store",
        });
        if (!res.ok) return;
        const data = await res.json().catch(() => ({}));
        const latest = normalize(String(data?.version || ""));
        const lastReloadedFor = sessionStorage.getItem(reloadedKey) || "";
        const lastPromptedFor = sessionStorage.getItem(promptedKey) || "";
        if (latest && current && latest !== current) {
          // If the app already reloaded for this version, do nothing
          if (lastReloadedFor === latest) return;

          // If we already prompted this session, avoid duplicate prompts
          if (lastPromptedFor === latest) return;

          try {
            sessionStorage.setItem(promptedKey, latest);
          } catch {}

          // Show an update toast with a Refresh action
          showToastWithProps({
            message: "A new version is available.",
            type: "info",
            position: "top",
            duration: 15000,
            actionText: "Refresh",
            onActionClick: () => {
              try {
                sessionStorage.setItem(reloadedKey, latest);
              } catch {}
              window.location.reload();
            },
          });
        }
      } catch {
        // Ignore transient network errors
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        check();
      }
    };

    // Initial check, then interval
    check();
    timer = window.setInterval(check, checkIntervalMs);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      if (timer) window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [currentVersion, checkIntervalMs]);

  return null;
}
